import {
  faInfoCircle,
  faListAlt,
  faShareAlt,
  faShieldAlt,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar from 'components/common/Avatar';
import CardDropdown from 'components/common/CardDropdown';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import IconAlert from 'components/common/IconAlert';
import Pagination from 'components/common/Pagination';
import SoftBadge from 'components/common/SoftBadge';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import corner3 from 'assets/img/illustrations/corner-3.png';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Dropdown,
  OverlayTrigger,
  Popover,
  Spinner,
  Tooltip
} from 'react-bootstrap';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';

import segmentHelper from 'helpers/segment-helper';
import { toast } from 'react-toastify';
import { setJournalEntryList } from 'stores/app';
import { showAlert, showConfirm, showViewEntryDialog } from 'stores/modal';
import Colors from 'util/Colors';
import api from 'util/api';
import c from 'util/const';
import filters from 'util/filters';
import logger from 'util/logger';
import NotaryJournalTableHeader from './NotaryJournalTableHeader';
import JournalBulkActions, {
  collectEntryGuidsFromRow
} from './JournalBulkActions';
import ShareJournalModal from './ShareJournalModal';
import draftEntryUiHelper from 'helpers/draft-entry-ui-helper';
import userHelper from 'helpers/userHelper';
import { journalApi, useGetJournalEntriesQuery } from 'rtk/journal.slice';
import SimpleBarReact from 'simplebar-react';
import './Journal.scss';

const SEARCH_DEBOUNCE_MS = 500;
const INITIAL_PAGE_CURSORS = { 0: null };
const TABLE_OVERLAY_PROPS = {
  container: typeof document !== 'undefined' ? document.body : undefined,
  popperConfig: { strategy: 'fixed' }
};
const notifyOverlaysOnScroll = () => {
  window.dispatchEvent(new Event('scroll'));
};

const getSigningSharedAdmins = entry => {
  const admins = Array.isArray(entry?.SharedWithAdmins)
    ? entry.SharedWithAdmins
    : [];
  return admins.filter(admin => {
    if (!admin) return false;
    if (typeof admin === 'string') return admin.trim().length > 0;
    return !!(
      admin.UserGuid ||
      admin.Email ||
      admin.OrganizationName ||
      admin.FullName
    );
  });
};

const isJournalShared = entry => {
  if (entry?.IsShared === true) return true;
  if (getSigningSharedAdmins(entry).length > 0) return true;
  const notaryEntries = Array.isArray(entry?.NotaryEntries)
    ? entry.NotaryEntries
    : [];
  return notaryEntries.some(
    item =>
      item?.IsShared === true ||
      (Array.isArray(item?.SharedWithAdmins) &&
        item.SharedWithAdmins.length > 0)
  );
};

const getSharedAdminParts = admin => {
  if (typeof admin === 'string') {
    return { name: admin, email: '' };
  }
  return {
    name:
      admin?.OrganizationName?.trim() || admin?.FullName?.trim() || 'Unknown',
    email: admin?.Email?.trim() || ''
  };
};

const Journal = () => {
  const [show, setShow] = useState(true);
  const [searchString, setSearchString] = useState(null);
  // Terms and the search window timestamp move together so a debounced search
  // issues exactly one request against a stable CreatedBefore boundary.
  const [search, setSearch] = useState({ terms: '', createdBefore: null });
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [endOfResults, setEndOfResults] = useState(false);
  // Cursor per visited page. Kept in query args so each page has its own cache entry.
  const [pageCursors, setPageCursors] = useState(INITIAL_PAGE_CURSORS);
  // Cursor advertised by the latest response. Deliberately NOT a query arg:
  // feeding it straight back into the args makes the query re-trigger itself.
  const [nextCursor, setNextCursor] = useState(null);
  const [filterMonth, setFilterMonth] = useState({});
  const [, setUpdatingChanges] = useState(false);
  const { userMaster, journalList, userProfile } = useSelector(
    state => state.app
  );
  const [customDateRange, setCustomDateRange] = useState(false);
  const [selectedCommissionProfile, setSelectedCommissionProfile] = useState();
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEntryGuids, setShareEntryGuids] = useState([]);

  const openShareModal = guids => {
    setShareEntryGuids(guids || []);
    setShowShareModal(true);
  };

  const openShareModalForRow = row => {
    openShareModal(collectEntryGuidsFromRow(row));
  };

  const closeShareModal = success => {
    setShowShareModal(false);
    setShareEntryGuids([]);
    if (success) {
      toast.success('Journal entries shared successfully.', {
        theme: 'colored',
        position: 'bottom-center'
      });
      refetch();
    }
  };

  const dispatch = useDispatch();
  const { subscriptionStatus } = useSelector(state => state.subscription);
  let activeSubscriptions = _.get(subscriptionStatus, 'Active', []);

  const trimmedTerms = search.terms;
  const hasSearch = trimmedTerms.length > 0;
  const skip = hasSearch ? 0 : pageIndex * pageSize;

  // Keep the input responsive; only the query terms are debounced.
  // Clearing the box applies immediately so browse mode returns without waiting.
  useEffect(() => {
    const nextTerms = (searchString || '').trim();
    if (!nextTerms) {
      setSearch(prev =>
        prev.terms === '' ? prev : { terms: '', createdBefore: null }
      );
      return undefined;
    }
    const handle = setTimeout(() => {
      setSearch({
        terms: nextTerms,
        createdBefore: moment().utc().format()
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchString]);

  const resetPaging = useCallback(() => {
    setPageIndex(0);
    setPageCursors(INITIAL_PAGE_CURSORS);
    setNextCursor(null);
  }, []);

  useEffect(() => {
    resetPaging();
    if (search.terms.length > 0) {
      setFilterMonth(null);
    }
  }, [search, resetPaging]);

  const queryArgs = useMemo(
    () => ({
      skip,
      pageSize,
      terms: trimmedTerms,
      selectedCommissionProfile: selectedCommissionProfile || '',
      filterMonthBegin: filterMonth?.begin ?? null,
      filterMonthEnd: filterMonth?.end ?? null,
      searchCreatedBefore: hasSearch ? search.createdBefore : null,
      searchPaging: hasSearch ? pageCursors[pageIndex] ?? null : null
    }),
    [
      skip,
      pageSize,
      trimmedTerms,
      selectedCommissionProfile,
      filterMonth?.begin,
      filterMonth?.end,
      hasSearch,
      search.createdBefore,
      pageCursors,
      pageIndex
    ]
  );

  const {
    data: journalRes,
    isFetching: isJournalFetching,
    isLoading: isJournalLoading,
    isError: journalLoadError,
    error: journalError,
    refetch
  } = useGetJournalEntriesQuery(queryArgs);

  const displayEntries = journalRes?.items || [];

  logger.log('Journal display entries loaded', displayEntries);

  // The typed text runs ahead of the debounced query terms. Treat that gap as
  // loading so the table shows feedback as soon as the user types.
  const searchPending = (searchString || '').trim() !== search.terms;
  const loading = isJournalFetching || isJournalLoading || searchPending;

  useEffect(() => {
    if (journalLoadError || journalError) {
      logger.log('error loading journal entries', journalError);
    }
  }, [journalLoadError, journalError]);

  useEffect(() => {
    // Ignore the gap while a new cache entry loads, otherwise the derived
    // paging state gets cleared and restored on every args change.
    if (!journalRes) return;
    const items = journalRes.items || [];
    dispatch(setJournalEntryList(items));
    setTotalRecords(journalRes.totalRecords ?? items.length);
    setEndOfResults(!!journalRes.endOfResults);
    setNextCursor(hasSearch ? journalRes.nextSearchPaging ?? null : null);
  }, [dispatch, journalRes, hasSearch]);

  useEffect(() => {
    logger.log('filterMonth', filterMonth);
    //setEntries([]);
    if (!customDateRange) {
      dispatch(setJournalEntryList(null)).then(() => {
        //logger.log('journalList1', store.getState().app.journalList);
        //logger.log('journalList2', journalList);

        initSearch(true);
      });
    }
  }, [filterMonth, selectedCommissionProfile]);

  const initSearch = refresh => {
    if (refresh) {
      resetPaging();
    }
  };

  const markUpdateItemChangesinEntry = (entry, options) => {
    dispatch(
      journalApi.util.updateQueryData('getJournalEntries', queryArgs, draft => {
        draft.items.forEach(item => {
          if (item.GUID === entry.GUID) {
            Object.assign(item, options);
          }
        });
      })
    );
  };

  const handleStrikeEntry = entry => {
    if (activeSubscriptions.length === 0) {
      dispatch(
        showAlert(
          'Subscription Required',
          'You have no active subscription and your signings are queued for deletion. Reactivate your subscription in the Subscription tab.'
        )
      );
      return;
    }
    if (entry.Stricken) {
      setUpdatingChanges(true);
      api.Signing.unstrike(entry.GUID)
        .then(res => {
          logger.log('unstrike res', res);
          segmentHelper.track(
            segmentHelper.events.SIGNING_DETAIL_UNSTRIKE_ENTRY
          );
          toast.success(`Signing record marked Unstriken`, {
            theme: 'colored',
            position: 'bottom-center',
            icon: faUserCheck,
            toastId: 'login-success'
          });
          markUpdateItemChangesinEntry(entry, { Stricken: false });
        })
        .catch(err => {
          logger.log('error while saving unstriking entry', err);
          dispatch(
            showAlert(
              'Unable to unstrike entry',
              'There was a problem unstriking your entry.  Please try again.'
            )
          );
        })
        .finally(() => {
          setUpdatingChanges(false);
        });
    } else {
      dispatch(
        showConfirm(
          'Are you sure?',
          'Striking a signing will cross it out in your journal history, but the entry will remain in your journal history.  You will be able to undo this action.',
          res => {
            if (res) {
              setUpdatingChanges(true);
              api.Signing.strike(entry.GUID)
                .then(() => {
                  //logger.log('strike res', res);
                  segmentHelper.track(
                    segmentHelper.events.SIGNING_DETAIL_STRIKE_ENTRY
                  );
                  toast.success(`Signing record marked Striken`, {
                    theme: 'colored',
                    position: 'bottom-center',
                    icon: faUserCheck,
                    toastId: 'login-success'
                  });
                  markUpdateItemChangesinEntry(entry, { Stricken: true });
                })
                .catch(err => {
                  logger.log('error while saving striking entry', err);
                  dispatch(
                    showAlert(
                      'Unable to strike entry',
                      'There was a problem striking your entry.  Please try again.'
                    )
                  );
                })
                .finally(() => {
                  setUpdatingChanges(false);
                });
            }
          }
        )
      );
    }
  };

  const columns = [
    {
      accessor: 'EmailSent',
      Header: 'Mail',
      headerProps: { className: 'pe-1' },
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { EmailSent, LocalTimezone } = rowData.row.original;
        return (
          <Flex alignItems="center">
            {EmailSent ? (
              <OverlayTrigger
                placement="top"
                {...TABLE_OVERLAY_PROPS}
                overlay={
                  <Tooltip>
                    Sent mail on:{' '}
                    {draftEntryUiHelper
                      .getDateTimeWithTimeZone(
                        EmailSent,
                        userHelper.getTimezoneVal(LocalTimezone)
                      )
                      .format(c.dateFormat)}
                  </Tooltip>
                }
              >
                <span>
                  <FontAwesomeIcon
                    size="xl"
                    color={Colors.success.color}
                    icon="envelope"
                    className="me-2"
                  />
                </span>
              </OverlayTrigger>
            ) : (
              <FontAwesomeIcon size="xl" icon="envelope" className="me-2" />
            )}
          </Flex>
        );
      }
    },
    {
      accessor: 'IsShared',
      Header: 'Shared',
      headerProps: { className: 'pe-1' },
      cellProps: {
        className: 'py-2'
      },
      disableSortBy: true,
      Cell: rowData => {
        const entry = rowData.row.original;
        if (!isJournalShared(entry)) {
          return <span className="text-400">—</span>;
        }
        const admins = getSigningSharedAdmins(entry);
        const overlay = (
          <Popover
            id={`shared-orgs-${entry.GUID}`}
            className="journal-shared-popover"
          >
            <Popover.Header as="h6" className="py-2 fs--1">
              Shared with
            </Popover.Header>
            <Popover.Body className="py-2 px-3 text-start">
              {admins.length > 0 ? (
                <ul className="journal-shared-list mb-0">
                  {admins.map(admin => {
                    const { name, email } = getSharedAdminParts(admin);
                    return (
                      <li
                        key={admin.UserGuid || admin.Email || name}
                        className="journal-shared-item"
                      >
                        <div className="journal-shared-name fw-bold">
                          {name}
                        </div>
                        {email ? (
                          <div className="journal-shared-email">{email}</div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="journal-shared-name">
                  Shared with organization
                </div>
              )}
            </Popover.Body>
          </Popover>
        );
        return (
          <OverlayTrigger
            placement="top"
            {...TABLE_OVERLAY_PROPS}
            overlay={overlay}
          >
            <span className="cursor-pointer">
              <SoftBadge pill bg="info" className="fs--2">
                <FontAwesomeIcon icon={faShareAlt} className="me-1" />
                {admins.length || ''}
              </SoftBadge>
            </span>
          </OverlayTrigger>
        );
      }
    },
    {
      accessor: 'SigningTimestamp',
      Header: 'Notarization Date and Time',
      Cell: rowData => {
        const { SigningTimestamp, Stricken, LocalTimezone } =
          rowData.row.original;
        let formattedDate = draftEntryUiHelper
          .getDateTimeWithTimeZone(
            SigningTimestamp,
            userHelper.getTimezoneVal(LocalTimezone)
          )

          .format(c.dateFormat);
        return (
          <span
            className={
              Stricken ? 'cursor-pointer text-danger' : 'cursor-pointer'
            }
            onClick={() => onRowClicked(rowData.row.original)}
            style={Stricken ? { textDecoration: 'line-through' } : {}}
          >
            {formattedDate}
          </span>
        );
      }
    },
    {
      accessor: 'Signers',
      Header: 'Signers',
      headerProps: { className: 'pe-1' },
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { Signers, Stricken } = rowData.row.original;
        let signerName = Signers ? filters.buildSignerString(Signers) : '';
        return signerName && signerName.length > 0 ? (
          <Flex
            alignItems="center"
            onClick={() => onRowClicked(rowData.row.original)}
          >
            <Avatar
              size="xl"
              name={signerName.substring(0, 10)}
              className="me-2"
              mediaClass={
                Stricken ? 'cursor-pointer bg-danger' : 'cursor-pointer'
              }
            />
            <div className="flex-1">
              <h5
                className={
                  Stricken
                    ? 'mb-0 fs--1 cursor-pointer text-danger'
                    : 'mb-0 fs--1 cursor-pointer'
                }
                style={Stricken ? { textDecoration: 'line-through' } : {}}
              >
                {userMaster.PrivacyEnabled
                  ? signerName.replace(/[a-zA-Z0-9]/g, '*').substring(0, 10)
                  : signerName}
              </h5>
            </div>
          </Flex>
        ) : (
          <h5
            className={
              Stricken
                ? 'mb-0 fs--1 ms-4 cursor-pointer text-danger'
                : 'mb-0 fs--1 ms-4 cursor-pointer'
            }
            style={Stricken ? { textDecoration: 'line-through' } : {}}
          >
            -
          </h5>
        );
      }
    },
    {
      accessor: 'NotaryEntries',
      Header: 'Entries',
      Cell: rowData => {
        const { NotaryEntries, Stricken } = rowData.row.original;
        return (
          <span
            className={
              Stricken ? 'cursor-pointer text-danger' : 'cursor-pointer'
            }
            style={Stricken ? { textDecoration: 'line-through' } : {}}
            onClick={() => onRowClicked(rowData.row.original)}
          >
            {NotaryEntries.length} Document
            {NotaryEntries?.length === 1 ? '' : 's'}
          </span>
        );
      }
    },
    {
      accessor: 'NotaryFee',
      Header: 'Notary Fee',
      Cell: rowData => {
        const { NotaryFee, NotaryFeeCurrency, Stricken } = rowData.row.original;
        return (
          <span
            className={
              Stricken ? 'cursor-pointer text-danger' : 'cursor-pointer'
            }
            style={Stricken ? { textDecoration: 'line-through' } : {}}
            onClick={() => onRowClicked(rowData.row.original)}
          >
            {NotaryFee === 0
              ? '$0'
              : NotaryFee
              ? userMaster.PrivacyEnabled
                ? '****'
                : filters.formatCurrencyFromServer(NotaryFee, NotaryFeeCurrency)
              : '- None entered -'}
          </span>
        );
      }
    },
    {
      accessor: 'State',
      Header: 'Address',
      Cell: rowData => {
        const { State, PostalCode, Stricken } = rowData.row.original;
        return (
          <span
            className={Stricken ? 'text-danger' : ''}
            style={Stricken ? { textDecoration: 'line-through' } : {}}
          >
            {State} - {PostalCode}
          </span>
        );
      }
    },

    {
      accessor: 'none',
      Header: '',
      disableSortBy: true,
      cellProps: {
        className: 'text-end'
      },
      Cell: rowData => {
        const { Stricken } = rowData.row.original;
        return (
          <CardDropdown drop="start" iconClassName="fs--1">
            <div className="py-2">
              <Dropdown.Item
                href="#!"
                onClick={() => onRowClicked(rowData.row.original)}
              >
                View
              </Dropdown.Item>
              <Dropdown.Item
                href="#!"
                onClick={() => handleStrikeEntry(rowData.row.original)}
              >
                {Stricken ? 'Unstrike' : 'Strike'}
              </Dropdown.Item>
              <Dropdown.Item
                href="#!"
                onClick={() => openShareModalForRow(rowData.row.original)}
              >
                <FontAwesomeIcon icon={faShareAlt} className="me-2" />
                Share with organization
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      }
    }
  ];

  const onRowClicked = entry => {
    if (activeSubscriptions.length === 0) {
      dispatch(
        showAlert(
          'Subscription Required',
          'You have no active subscription and your signings are queued for deletion. Reactivate your subscription in the Subscription tab.'
        )
      );
      return;
    }

    dispatch(
      showViewEntryDialog(entry.GUID, res => {
        if (res) {
          initializeEntries();
        }
      })
    );
  };

  const initializeEntries = refresh => {
    // Listing is now driven by RTK Query args; keep this for call sites.
    if (refresh) {
      resetPaging();
    }
  };

  // const doSortEntriesForDisplay = entries => {
  //   logger.log('Sorting entries', entries);
  //   //Watch for changes here.  This should reflect what is displayed, in DashboardItem.
  //   return _.sortBy(entries, entry => {
  //     if (entry.SigningTimestamp) {
  //       return moment(entry.SigningTimestamp).unix();
  //     } else if (entry.Completed) {
  //       return -moment(entry.Completed).unix();
  //     } else {
  //       return -moment(entry.Created).unix();
  //     }
  //   });
  // };

  const handlePageChange = nextPageIndex => {
    if (loading || nextPageIndex === pageIndex) return;

    if (hasSearch) {
      // Search is cursor paged: only advance one page at a time, and only to
      // pages whose CreatedBefore cursor is already known.
      if (nextPageIndex === pageIndex + 1) {
        if (!nextCursor) return;
        setPageCursors(prev => ({ ...prev, [nextPageIndex]: nextCursor }));
        setPageIndex(nextPageIndex);
        return;
      }
      if (pageCursors[nextPageIndex] !== undefined) {
        setPageIndex(nextPageIndex);
      }
      return;
    }

    setPageIndex(nextPageIndex);
  };

  const handlePageSizeChange = nextPageSize => {
    setPageSize(nextPageSize);
    resetPaging();
  };

  const onCustomDateRange = isFilter => {
    if (isFilter) {
      dispatch(setJournalEntryList(null)).then(() => {
        initSearch(true);
      });
    } else {
      setCustomDateRange(false);
      setFilterMonth({});
    }
  };

  const isProfileActive = profile => {
    return (
      profile?.Active &&
      moment()
        .startOf('day')
        .isSameOrBefore(moment(profile?.ExpirationDate).utc())
    );
  };

  const getDisplayProfiles = () => {
    const profiles = userProfile || [];
    const defaults = profiles.filter(p => p?.IsDefault);
    const actives = profiles.filter(p => !p?.IsDefault && isProfileActive(p));
    return [...defaults, ...actives] || [];
  };

  const changeCommissionProfile = value => {
    if (setSelectedCommissionProfile) {
      setSelectedCommissionProfile(value);
    }
  };

  /* eslint-disable react/prop-types */
  const IconAlertCode = function IconAlertDemo({ variant, message }) {
    if (show) {
      return (
        <IconAlert variant={variant} dismissible onClose={() => setShow(false)}>
          <p className="mb-0">{message}</p>
        </IconAlert>
      );
    }
    return null;
  };
  /* eslint-enable react/prop-types */

  const getSelectedProfileTitle = () => {
    if (!selectedCommissionProfile) {
      return 'Show all commission profiles';
    }
    const profiles = getDisplayProfiles();
    const selected = profiles.find(p => p.GUID === selectedCommissionProfile);
    if (selected) {
      return `${selected.CommissionedName} ${selected.CommissionNumber} / ${
        userHelper.getTimezoneObj(selected.LocalTimezone)?.name
      }`;
    }
    return 'Select commission profile';
  };

  // Search has no total count, so the cursor tells us whether another page exists.
  const atEndOfResults = hasSearch ? !nextCursor : endOfResults;
  const paginationTotalRecords = hasSearch
    ? pageSize * pageIndex + displayEntries.length + (nextCursor ? pageSize : 0)
    : totalRecords;
  logger.log(
    'Journal pagination total records calculated',
    paginationTotalRecords,
    isJournalLoading
  );

  return (
    <>
      {!loading && activeSubscriptions.length === 0 && (
        <div className="pb-3">
          <Alert variant="warning">
            <Alert.Heading>
              <FontAwesomeIcon icon={faInfoCircle} /> Subscription Required
            </Alert.Heading>
            <p>
              You have no active subscription and your signings are queued for
              deletion. Reactivate your subscription in the Subscription tab.
            </p>
          </Alert>
        </div>
      )}

      {
        <AdvanceTableWrapper
          columns={columns}
          data={journalRes?.items || []}
          selection={activeSubscriptions.length > 0}
          selectionColumnWidth={40}
          sortable
          pagination
          perPage={pageSize}
          sortBy={[]}
        >
          <JournalBulkActions
            table
            onShareClick={openShareModal}
            disabled={loading || activeSubscriptions.length === 0}
          />
          <FalconComponentCard noGuttersBottom>
            <FalconComponentCard.Header
              image={corner3}
              className={'px-4'}
              title="Journal Entries"
              subtitle="Browse your notary journal entries"
              endEl={
                <Flex direction="column" alignItems="end" className="gap-2">
                  {userMaster?.PrivacyEnabled && (
                    <SoftBadge pill bg="success" className="me-2">
                      <FontAwesomeIcon
                        size="sm"
                        color={Colors.success.color}
                        icon={faShieldAlt}
                        className="me-0"
                      />{' '}
                      Privacy mode
                    </SoftBadge>
                  )}
                  <NotaryJournalTableHeader
                    table
                    searchString={searchString}
                    setSearchString={setSearchString}
                    filterMonth={filterMonth}
                    setFilterMonth={setFilterMonth}
                    onCustomDateRange={onCustomDateRange}
                    customDateRange={customDateRange}
                    setCustomDateRange={setCustomDateRange}
                  />
                </Flex>
              }
            />
            <div noLight className="py-0 px-4">
              {isJournalLoading && (
                <Flex justifyContent="center" className="py-3">
                  <Spinner size="sm" />
                </Flex>
              )}
              {/* <IconAlertCode variant="info" message="Any saved drafts you may have are stored on your device only, until they are completed and uploaded to our servers." /> */}
              {loading && !isJournalLoading && displayEntries.length === 0 && (
                <Flex justifyContent="center" className="py-3">
                  <Spinner size="sm" />
                </Flex>
              )}
              {!isJournalLoading && (
                <>
                  <IconAlertCode
                    variant="info"
                    message="Your journal is sequential based on the date & time you selected when creating a new entry. If you did not manually select a date & time while creating the entry, we use the 'completed' time stamp of the entry."
                  />
                  <Dropdown align="start" className="filter-dropdown mt-1 mb-3">
                    <Dropdown.Toggle
                      variant="outline-primary"
                      size="sm"
                      id="commission-profile-dropdown"
                      style={{ minWidth: '200px' }}
                    >
                      {getSelectedProfileTitle()}
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      className="filter-dropdown-menu"
                      style={{ minWidth: '200px' }}
                    >
                      <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        {getDisplayProfiles().length === 0 ? (
                          <>
                            <Dropdown.Item disabled>
                              No active Commission Profiles available.
                            </Dropdown.Item>
                            <Dropdown.Item disabled>
                              Please add new profile from Notary Profile
                              Commission.
                            </Dropdown.Item>
                          </>
                        ) : (
                          <>
                            {getDisplayProfiles().map(option => (
                              <Dropdown.Item
                                key={option.GUID}
                                className={
                                  selectedCommissionProfile === option.GUID
                                    ? 'text-primary fw-semi-bold active'
                                    : ''
                                }
                                onClick={() =>
                                  changeCommissionProfile(option.GUID)
                                }
                                eventKey={option.GUID}
                              >
                                {option.CommissionedName}{' '}
                                {option.CommissionNumber} /{' '}
                                {
                                  userHelper.getTimezoneObj(
                                    option.LocalTimezone
                                  )?.name
                                }
                              </Dropdown.Item>
                            ))}
                            <Dropdown.Divider />
                            <Dropdown.Item
                              eventKey="clear"
                              onClick={() => changeCommissionProfile('')}
                              className={
                                !selectedCommissionProfile
                                  ? 'text-primary fw-semi-bold'
                                  : ''
                              }
                            >
                              Show All
                            </Dropdown.Item>
                          </>
                        )}
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                  {(paginationTotalRecords > 0 || displayEntries.length > 0) &&
                    !isJournalLoading && (
                      <Pagination
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalRecords={paginationTotalRecords}
                        currentCount={displayEntries.length}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        endOfResults={atEndOfResults}
                        disabled={loading}
                        className="px-1"
                        onRefresh={() => refetch()}
                        loading={loading}
                      />
                    )}
                  <SimpleBarReact
                    scrollableNodeProps={{ onScroll: notifyOverlaysOnScroll }}
                  >
                    <AdvanceTable
                      table
                      headerClassName="bg-200 text-900 text-nowrap align-middle"
                      rowClassName={`align-middle white-space-nowrap ${
                        activeSubscriptions.length == 0 && 'block-text-blur'
                      }`}
                      tableProps={{
                        size: 'sm',
                        striped: true,
                        className: `fs--1 mb-0 overflow-hidden${
                          loading ? ' refreshing-skeleton' : ''
                        }`
                      }}
                      onRowClicked={onRowClicked}
                    />
                  </SimpleBarReact>
                </>
              )}
            </div>
          </FalconComponentCard>
        </AdvanceTableWrapper>
      }

      <ShareJournalModal
        show={showShareModal}
        onHide={closeShareModal}
        entryGuids={shareEntryGuids}
        entryCount={shareEntryGuids.length}
      />

      {!loading &&
        journalList?.length === 0 &&
        !((searchString || '').trim().length > 0) && (
          <>
            <Card className="text-center py-3 mb-3">
              <Card.Body className="p-0">
                <div className="text-center">
                  <FontAwesomeIcon
                    className="text-muted"
                    icon={faListAlt}
                    size="3x"
                  />
                  <br />
                  <p className="my-3">You have no journal entries.</p>
                </div>
              </Card.Body>
            </Card>
            <Card className="text-center py-5">
              <Card.Body className="p-0">
                <h4 className="display-5">
                  Welcome to The Notary eJournal, by Jurat inc
                </h4>
                <p className="pt-3">
                  It looks like you haven't created any Journal Entries yet.
                  Once you do, they will show up here and you can view them.
                </p>
                <div className="text-center mt-3">
                  <div className="text-muted">
                    If you haven't already, you can download the app below.
                  </div>
                  <Flex
                    direction="row"
                    justifyContent="center"
                    className="mt-3"
                  >
                    <div className="text-center mb-2 me-2">
                      <button
                        className="btn btn-outline-dark btn-icon-text"
                        onClick={() => window.open(c.mobileApp.store.ios)}
                      >
                        <FaApple
                          className="btn-icon-prepend mr-2"
                          style={{
                            verticalAlign: 'baseline',
                            fontSize: '40px'
                          }}
                        />
                        <span className="d-inline-block text-left">
                          {' '}
                          <small className="font-weight-light d-block">
                            Available on the
                          </small>{' '}
                          App Store{' '}
                        </span>
                      </button>
                    </div>

                    <div className="text-center mb-2 me-2">
                      <button
                        className="btn btn-outline-dark btn-icon-text"
                        onClick={() => window.open(c.mobileApp.store.android)}
                      >
                        <FaGooglePlay
                          className="btn-icon-prepend mr-2"
                          style={{
                            verticalAlign: 'baseline',
                            fontSize: '40px'
                          }}
                        />
                        <span className="d-inline-block text-left">
                          {' '}
                          <small className="font-weight-light d-block">
                            Get it on
                          </small>{' '}
                          Google Play{' '}
                        </span>
                      </button>
                    </div>
                  </Flex>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
    </>
  );
};

export default Journal;
